// OAuth Provider types
export type OAuthProvider = 'google' | 'github' | 'microsoft' | 'okta' | 'apple';

export const OAUTH_PROVIDERS: OAuthProvider[] = ['google', 'github', 'microsoft', 'okta', 'apple'];

export const PROVIDER_DISPLAY_NAMES: Record<OAuthProvider, string> = {
  google: 'Google',
  github: 'GitHub',
  microsoft: 'Microsoft',
  okta: 'Okta',
  apple: 'Apple',
};

// OAuth Configuration
export interface OAuthConfig {
  provider: OAuthProvider;
  client_id: string;
  client_secret_masked: string;
  scopes: string[];
  redirect_uri?: string;
  enabled: boolean;
  tested_at: string | null;
  provider_settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AvailableProvider {
  provider: OAuthProvider;
  configured: boolean;
  enabled?: boolean;
}

// Provider-specific settings
export interface GoogleProviderSettings {
  hosted_domain?: string;
  prompt?: 'none' | 'consent' | 'select_account';
}

export interface MicrosoftProviderSettings {
  tenant_id?: string; // 'common', 'organizations', 'consumers', or specific tenant UUID
}

export interface OktaProviderSettings {
  issuer: string;
  authorization_server?: string;
}

export interface GitHubProviderSettings {
  allowed_organizations?: string[];
}

export interface AppleProviderSettings {
  team_id?: string;
  key_id?: string;
}

export type ProviderSettings =
  | GoogleProviderSettings
  | MicrosoftProviderSettings
  | OktaProviderSettings
  | GitHubProviderSettings
  | AppleProviderSettings;

// API Request types
export interface CreateOAuthConfigRequest {
  provider: OAuthProvider;
  client_id: string;
  client_secret: string;
  scopes?: string[];
  redirect_uri?: string;
  provider_settings?: Record<string, unknown>;
}

export interface UpdateOAuthConfigRequest {
  client_id?: string;
  client_secret?: string;
  scopes?: string[];
  redirect_uri?: string;
  enabled?: boolean;
  provider_settings?: Record<string, unknown>;
}

export interface BulkImportConfig {
  provider: OAuthProvider;
  client_id: string;
  client_secret: string;
  scopes?: string[];
  provider_settings?: Record<string, unknown>;
}

export interface BulkImportRequest {
  configs: BulkImportConfig[];
}

// API Response types
export interface OAuthConfigsListResponse {
  success: boolean;
  data?: {
    configured: OAuthConfig[];
    available: AvailableProvider[];
  };
  error?: string;
}

export interface OAuthConfigResponse {
  success: boolean;
  data?: OAuthConfig;
  message?: string;
  warning?: string;
  error?: string;
  details?: string[];
}

export interface TestConnectionResponse {
  success: boolean;
  data?: {
    tested_at: string;
    provider_info: {
      issuer?: string;
      authorization_endpoint?: string;
      token_endpoint?: string;
      userinfo_endpoint?: string;
      scopes_supported?: string[];
      api_url?: string;
      user_endpoint?: string;
    };
  };
  message?: string;
  error?: string;
  details?: string[];
}

export interface RedirectUriResponse {
  success: boolean;
  data?: {
    redirect_uri: string;
    provider: OAuthProvider;
  };
  error?: string;
  details?: string[];
}

export interface BulkImportResult {
  provider: OAuthProvider;
  status: 'created' | 'failed';
  error?: string;
}

export interface BulkImportResponse {
  success: boolean;
  data?: {
    created: number;
    failed: number;
    results: BulkImportResult[];
  };
  error?: string;
}

export interface DeleteOAuthConfigResponse {
  success: boolean;
  message?: string;
  error?: string;
}

// Default scopes per provider
export const DEFAULT_SCOPES: Record<OAuthProvider, string[]> = {
  google: ['email', 'profile', 'openid'],
  github: ['user:email', 'read:user'],
  microsoft: ['openid', 'email', 'profile', 'User.Read'],
  okta: ['openid', 'email', 'profile'],
  apple: ['name', 'email'],
};

// Provider documentation URLs
export const PROVIDER_DOCS: Record<OAuthProvider, string> = {
  google: 'https://console.cloud.google.com/apis/credentials',
  github: 'https://github.com/settings/developers',
  microsoft: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade',
  okta: 'https://developer.okta.com/docs/guides/implement-oauth-for-okta/',
  apple: 'https://developer.apple.com/account/resources/identifiers/list/serviceId',
};
